import i18n from 'i18next';
import HttpBackend, { type RequestCallback } from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

// note8 Task 5 — only fully-translated (100%) languages are exposed in the UI.
// Kurdish ('ku') and Albanian ('al') are temporarily hidden until their
// translations are complete; re-enable by adding their codes back here. This is
// the single source of truth for every UI language selector AND i18next
// `supportedLngs`, so stale `ku`/`al` selections fall back to `fallbackLng`.
export const SUPPORTED_LANGUAGES = ['ar', 'en'] as const;
export type Language = (typeof SUPPORTED_LANGUAGES)[number];

export const RTL_LANGUAGES: ReadonlySet<string> = new Set(['ar', 'ku']);

export function directionFor(lng: string): 'rtl' | 'ltr' {
  return RTL_LANGUAGES.has(lng.toLowerCase()) ? 'rtl' : 'ltr';
}

/**
 * Phase 12: translations are served from the database via /api/i18n/{lng}/{ns}.
 * If the API is unreachable (offline backend, network blip, dev with API down),
 * we transparently fall back to the legacy static files at /lang/*.json so the
 * UI never goes blank.
 */
async function loadFromApi(lng: string, ns: string): Promise<Record<string, string>> {
  const res = await fetch(`/api/i18n/${lng}/${ns}`, { credentials: 'omit' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as Record<string, string>;
}

async function loadFromStaticFile(lng: string, ns: string): Promise<Record<string, string>> {
  const url = ns === 'common' ? `/lang/${lng}.json` : `/lang/${lng}_${ns}.json`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`fallback HTTP ${res.status}`);
  return (await res.json()) as Record<string, string>;
}

// notes7 #6 (part 6): the system standardises on Latin/Western digits everywhere.
// Some translation strings interpolate raw numbers via plain `{{count}}` (which
// the `number` formatter override does NOT touch), so Arabic locales can still
// render Arabic-Indic digits (e.g. the notification relative-time "قبل ٢ ساعة").
// A global post-processor normalises Arabic-Indic (٠-٩) and Persian (۰-۹) digits
// to Latin (0-9) on the FINAL rendered string — independent of catalog/DB state,
// so it covers relative-time, unread counts, and any other numeric interpolation
// in one place. It only rewrites digit characters; Arabic words stay intact.
const latinDigitsPostProcessor = {
  type: 'postProcessor' as const,
  name: 'latinDigits',
  process(value: string): string {
    if (typeof value !== 'string') return value;
    return value
      .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
      .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0));
  },
};

void i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(latinDigitsPostProcessor)
  .use(initReactI18next)
  .init({
    postProcess: ['latinDigits'],
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    ns: ['common', 'superadmin'],
    defaultNS: 'common',
    backend: {
      // loadPath is required by the type but our custom request below is what runs.
      loadPath: '/api/i18n/{{lng}}/{{ns}}',
      request: (_options: unknown, url: string, _payload: unknown, callback: RequestCallback) => {
        const m = url.match(/\/api\/i18n\/([^/]+)\/([^/]+)/);
        if (!m) {
          callback(new Error(`unrecognized i18n url: ${url}`), undefined as never);
          return;
        }
        const [, lng, ns] = m;
        loadFromApi(lng, ns)
          .then((data) => callback(null, { status: 200, data: JSON.stringify(data) }))
          .catch(() =>
            loadFromStaticFile(lng, ns)
              .then((data) => callback(null, { status: 200, data: JSON.stringify(data) }))
              .catch((err) => callback(err as Error, undefined as never)),
          );
      },
    },
    detection: {
      order: ['localStorage', 'htmlTag', 'navigator'],
      lookupLocalStorage: 'i18nextLng',
      caches: ['localStorage'],
    },
    interpolation: { escapeValue: false },
    returnEmptyString: false,
    saveMissing: import.meta.env.DEV,
    missingKeyHandler: (_lngs, ns, key) => {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn(`[i18n] missing key: ${ns}:${key}`);
        // Fire-and-forget POST so backend can collect missing keys for the
        // admin UI. The endpoint may not exist yet — failures are silent.
        void fetch('/api/i18n/admin/missing', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ key, ns }),
          credentials: 'omit',
        }).catch(() => undefined);
      }
    },
    react: { useSuspense: false },
  });

// notes7 #5: force Latin/English numerals for the `number` formatter. i18next's
// built-in `number` formatter uses Intl.NumberFormat(lng), which renders
// Arabic-Indic digits for `ar` (e.g. the notification relative-time
// "قبل {{count, number}} ساعة" → "قبل ٢ ساعة"). The system standardises on Latin
// digits, so render numbers with the `en` locale regardless of UI language. Only
// the `number` formatter is overridden — `currency`/`datetime`/etc. keep their
// locale-aware defaults.
i18n.on('initialized', () => {
  i18n.services?.formatter?.add('number', (value, _lng, options) => {
    if (typeof value !== 'number') return String(value ?? '');
    try {
      return new Intl.NumberFormat('en', options).format(value);
    } catch {
      return String(value);
    }
  });
});

i18n.on('languageChanged', (lng) => {
  const dir = directionFor(lng);
  document.documentElement.setAttribute('lang', lng);
  document.documentElement.setAttribute('dir', dir);
});

export default i18n;
